/**
 * LightGlassPreset — Headlight and taillight glass materials.
 *
 * Used by LightSignatureManager to replace the original GLB glass mesh
 * materials so the light signature (emissive mesh behind) is visible
 * through the lens.
 *
 * Strategy:
 *   - MeshStandardMaterial — no clearcoat. MeshPhysicalMaterial clearcoat
 *     causes angle-dependent env-map mirror reflections that look broken
 *     on desktop wide viewports in all browsers.
 *   - `side: THREE.DoubleSide` — original GLB glass has doubleSided:true.
 *     Without this, back-faces are culled at certain camera angles,
 *     producing black holes (especially in Chrome/ANGLE on desktop).
 *   - `depthWrite: false` → no z-buffer write (renderOrder handles sorting)
 *   - Mesh renderOrder is set by LightSignatureManager:
 *       glass → 5 (after opaque body, before light mesh at 10)
 *
 * @see packages/engine/src/core/LightSignatureManager.ts
 * @see LIGHT_SIGNATURE_GUIDE.md — render-order rationale
 */

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Front headlight glass — dark / neutral
// ---------------------------------------------------------------------------

export function createHeadlightGlassMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    name: 'IMS_LightGlass_Front',
    color: new THREE.Color('#111111'),
    roughness: 0.02,
    metalness: 0.0,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

// ---------------------------------------------------------------------------
// Rear taillight glass — dark red
// ---------------------------------------------------------------------------

export function createRearlightGlassMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    name: 'IMS_LightGlass_Rear',
    color: new THREE.Color('#400000'),
    emissive: new THREE.Color('#0f0000'),
    emissiveIntensity: 0.4,
    roughness: 0.15,
    metalness: 0.0,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}
